import { ReactNode } from 'react'
import { FormGroup, Modal } from 'react-bootstrap'
import FormHandler, { FormParams } from '../../utils/Form/FormHandler'
import Field from '../../utils/Form/Widgets/Field'
import AutocompleteSelect from '../../utils/Form/Widgets/AutocompleteSelect'
import Select from '../../utils/Form/Widgets/Select'
import { companySitesList } from '../charts/core/_requests'
import axios from 'axios'
import ReactSelect from 'react-select'
import { DateRange, Range, RangeKeyDict } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import moment from 'moment'
import Swal from 'sweetalert2'

type Props = {
  onHide: () => void
  show: boolean
  itemIdForUpdate?: string
  refreshFunction?: Function
}

class Simulations {
  name?: string
  description?: string
  company?: number
  company_site?: number
  meter_type?: 'Electricity' | 'Gas'
  start_date?: string
  end_date?: string
  profiles?: string
  use_profile_reference?: boolean
  simulation_settings?: string
  status?: 'created' | 'processing' | 'success' | 'failed'
  result_data?: string
  detail?: string
}

class ModalState {
  siteOptions?: {
    value: string
    label: string
  }[]
  profileOptions?: {
    value: number
    label: string
    profile_type: string
    event_type_name?: string
    day?: string
  }[]
  selectedProfiles?: any[]
  calendarSettings?: {
    [date: string]: {
      profiles: number[] | null
    }
  }
  dateRange?: Range
  datePickerOpen?: boolean
}

class SimulationsEditModal extends FormHandler {
  public state: FormParams & ModalState

  constructor(props: Props) {
    super(props)
    const initial = new Simulations()
    ;(initial as any).status = 'created'
    ;(initial as any).use_profile_reference = false
    this.state = {
      entity: initial,
      siteOptions: [],
      profileOptions: [],
      selectedProfiles: [],
      calendarSettings: {},
      dateRange: {
        startDate: undefined,
        endDate: undefined,
        key: 'selection',
      },
      datePickerOpen: false,
    }
  }

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{}>, snapshot?: any): void {
    super.componentDidUpdate(prevProps, prevState, snapshot)
    let state = this.state as any
    let prev = prevState as any
    if (prev.entity.company !== state.entity.company) {
      this.getSites()
      this.getProfiles()
    }
    if (!prev.entity.company && state.entity.company) {
      this.getSites()
      this.getProfiles()
    }
    if (prev.entity.company_site !== state.entity.company_site) {
      this.getProfiles()
    }
    if (prev.entity.meter_type !== state.entity.meter_type) {
      this.getProfiles()
    }
    // Load selected profiles when profiles field is loaded
    if (
      !prev.entity.profiles &&
      state.entity.profiles &&
      this.state.selectedProfiles?.length === 0
    ) {
      try {
        const parsed =
          typeof state.entity.profiles === 'string'
            ? JSON.parse(state.entity.profiles)
            : state.entity.profiles
        const profileIds = parsed.profile_ids || []
        const selected = (this.state.profileOptions || []).filter((opt) =>
          profileIds.includes(opt.value)
        )
        this.setState({
          ...this.state,
          selectedProfiles: selected,
        })
      } catch (e) {
        console.error('Failed to parse profiles', e)
      }
    }
    // Update selected profiles when profileOptions change
    if (
      prev.profileOptions !== this.state.profileOptions &&
      state.entity.profiles &&
      this.state.profileOptions &&
      this.state.profileOptions.length > 0
    ) {
      try {
        const parsed =
          typeof state.entity.profiles === 'string'
            ? JSON.parse(state.entity.profiles)
            : state.entity.profiles
        const profileIds = parsed.profile_ids || []
        const selected = (this.state.profileOptions || []).filter((opt) =>
          profileIds.includes(opt.value)
        )
        if (selected.length > 0 && this.state.selectedProfiles?.length === 0) {
          this.setState(
            {
              ...this.state,
              selectedProfiles: selected,
            },
            () => {
              // After selectedProfiles are set, parse simulation_settings if available
              if (
                state.entity.simulation_settings &&
                Object.keys(this.state.calendarSettings || {}).length === 0
              ) {
                this.parseExistingSimulationSettings()
              }
            }
          )
        }
      } catch (e) {
        console.error('Failed to parse profiles', e)
      }
    }
  }

  getSites() {
    let state = this.state as any
    if (state.entity.company) {
      companySitesList({ companyId: Number(state.entity.company) }).then((response) => {
        this.setState({
          ...this.state,
          siteOptions: response,
        })
      })
    } else {
      this.setState({
        ...this.state,
        siteOptions: [],
      })
    }
  }

  getProfiles() {
    let state = this.state as any
    if (state.entity.company) {
      const params: any = {
        company: Number(state.entity.company),
      }
      if (state.entity.company_site) {
        params.company_sites = Number(state.entity.company_site)
      }
      if (state.entity.meter_type) {
        params.meter_type = state.entity.meter_type
      }
      axios.get('/profiles/getProfilesForSelect', { params }).then((response) => {
        const profiles = response.data.data || []
        const options = profiles.map((profile: any) => {
          let profileType = 'Day Based'
          if (profile.profile_type === 'event_based') {
            profileType = 'Event Based'
          }
          const detail =
            profile.profile_type === 'event_based'
              ? profile.event_type_name || ''
              : profile.day || ''
          const label = `${profile.name} (${profileType} - ${detail})`
          return {
            value: profile.ID,
            label: label,
            profile_type: profile.profile_type,
            event_type_name: profile.event_type_name,
            day: profile.day,
          }
        })
        this.setState({
          ...this.state,
          profileOptions: options,
        })
      })
    } else {
      this.setState({
        ...this.state,
        profileOptions: [],
      })
    }
  }

  getApiUrl(): string {
    return '/simulations/save/' + this.props.itemIdForUpdate
  }
  getSingleUrl() {
    if (this.props.itemIdForUpdate === 'add') {
      return null
    } else {
      return '/simulations/single/' + this.props.itemIdForUpdate
    }
  }

  getPostData(): object {
    const state = this.state as any
    // Convert boolean to "yes"/"no" for backend
    const entityForPost = {
      ...state.entity,
      use_profile_reference: state.entity.use_profile_reference ? 'yes' : 'no',
    }

    // If use_profile_reference is false, ensure profiles and simulation_settings are empty
    if (!state.entity.use_profile_reference) {
      entityForPost.profiles = JSON.stringify({ profile_ids: [] })
      entityForPost.simulation_settings = '{}'
    }

    return {
      simulations: entityForPost,
    }
  }

  getObjectData(): void {
    let singleUrl = this.getSingleUrl()
    if (singleUrl) {
      axios.get(singleUrl).then((response) => {
        const entityData = response.data.data
        // Convert "yes"/"no" to boolean for UI
        if (entityData.use_profile_reference === 'yes') {
          entityData.use_profile_reference = true
        } else if (entityData.use_profile_reference === 'no') {
          entityData.use_profile_reference = false
        }
        // Set dateRange from entity dates
        const dateRange: Range = {
          startDate: entityData.start_date ? new Date(entityData.start_date) : undefined,
          endDate: entityData.end_date ? new Date(entityData.end_date) : undefined,
          key: 'selection',
        }
        this.setState(
          {
            entity: entityData,
            dateRange: dateRange,
          },
          () => {
            // After entity is loaded, load sites and profiles
            this.getSites()
            this.getProfiles()
          }
        )
      })
    }
  }

  generateCalendarDates(): string[] {
    const state = this.state as any
    if (!state.entity.start_date || !state.entity.end_date) {
      return []
    }
    const start = new Date(state.entity.start_date)
    const end = new Date(state.entity.end_date)
    const dates: string[] = []
    const current = new Date(start)
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0]
      dates.push(dateStr)
      current.setDate(current.getDate() + 1)
    }
    return dates
  }

  initializeCalendarWithDayBased() {
    const dates = this.generateCalendarDates()
    const oldSettings = this.state.calendarSettings || {}
    const newCalendarSettings: any = {}

    const selectedProfileIds = this.state.selectedProfiles?.map((p) => p.value) || []
    const dayBasedProfiles = this.state.selectedProfiles?.filter(
      (p) => p.profile_type === 'day_based'
    )

    dates.forEach((dateStr) => {
      const date = new Date(dateStr)
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' })

      // Keep existing profiles ONLY IF they are still among selected profiles
      const existingProfiles = (oldSettings[dateStr]?.profiles || []).filter((id: number) =>
        selectedProfileIds.includes(id)
      )

      newCalendarSettings[dateStr] = { profiles: [...existingProfiles] }

      // Auto-add/Ensure day_based profiles for this day are included
      dayBasedProfiles?.forEach((profile) => {
        if (profile.day === dayName) {
          if (!newCalendarSettings[dateStr].profiles.includes(profile.value)) {
            newCalendarSettings[dateStr].profiles.push(profile.value)
          }
        }
      })
    })

    this.setState(
      {
        ...this.state,
        calendarSettings: newCalendarSettings,
      },
      () => {
        // Update simulation_settings in entity
        const syntheticEvent = {
          target: {
            name: 'simulation_settings',
            value: JSON.stringify(newCalendarSettings),
            type: 'text',
          },
        } as any
        this.handleInputChange(syntheticEvent)
      }
    )
  }

  addEventToDate(dateStr: string, profileId: number) {
    const newCalendarSettings: any = { ...this.state.calendarSettings }

    if (!newCalendarSettings[dateStr]) {
      newCalendarSettings[dateStr] = { profiles: [] }
    }

    if (!newCalendarSettings[dateStr].profiles) {
      newCalendarSettings[dateStr].profiles = []
    }

    if (!newCalendarSettings[dateStr].profiles.includes(profileId)) {
      newCalendarSettings[dateStr].profiles.push(profileId)
    }

    this.setState({
      ...this.state,
      calendarSettings: newCalendarSettings,
    })

    // Update simulation_settings in entity
    const syntheticEvent = {
      target: {
        name: 'simulation_settings',
        value: JSON.stringify(newCalendarSettings),
        type: 'text',
      },
    } as any
    this.handleInputChange(syntheticEvent)
  }

  removeEventFromDate(dateStr: string, profileId: number) {
    const newCalendarSettings: any = { ...this.state.calendarSettings }

    if (!newCalendarSettings[dateStr] || !newCalendarSettings[dateStr].profiles) {
      return
    }

    const index = newCalendarSettings[dateStr].profiles.indexOf(profileId)
    if (index > -1) {
      newCalendarSettings[dateStr].profiles.splice(index, 1)
    }

    this.setState({
      ...this.state,
      calendarSettings: newCalendarSettings,
    })

    // Update simulation_settings in entity
    const syntheticEvent = {
      target: {
        name: 'simulation_settings',
        value: JSON.stringify(newCalendarSettings),
        type: 'text',
      },
    } as any
    this.handleInputChange(syntheticEvent)
  }

  parseExistingSimulationSettings() {
    const state = this.state as any
    if (state.entity.simulation_settings) {
      try {
        const parsed =
          typeof state.entity.simulation_settings === 'string'
            ? JSON.parse(state.entity.simulation_settings)
            : state.entity.simulation_settings
        this.setState({
          ...this.state,
          calendarSettings: parsed,
        })
      } catch (e) {
        console.error('Failed to parse simulation_settings', e)
      }
    }
  }

  getProfileName(profile: any): string {
    // Extract profile name from label format: "Profile Name (Type - Detail)"
    const label = profile.label || ''
    const match = label.match(/^(.+?)\s*\(/)
    return match ? match[1].trim() : label
  }

  getProfileInitials(profileName: string): string {
    // Get initials from profile name
    return profileName
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
  }

  handleCustomDateRangeChange(rangesByKey: RangeKeyDict) {
    const range = rangesByKey.selection
    if (!range) return

    this.setState({
      ...this.state,
      dateRange: range,
    })
  }

  applyDateRange() {
    const range = this.state.dateRange
    if (range && range.startDate && range.endDate) {
      const start = moment(range.startDate)
      const end = moment(range.endDate)
      const daysDiff = end.diff(start, 'days')

      // Check if range exceeds 1 month
      if (daysDiff > 30) {
        Swal.fire({
          title: 'Invalid Range',
          text: 'Maximum date range is 1 month (31 days). Please select a shorter range.',
          icon: 'warning',
          confirmButtonText: 'OK',
        })
        return
      }

      // Update entity dates
      const startDateStr = start.format('YYYY-MM-DD')
      const endDateStr = end.format('YYYY-MM-DD')

      this.setState(
        {
          ...this.state,
          datePickerOpen: false,
          entity: {
            ...this.state.entity,
            start_date: startDateStr,
            end_date: endDateStr,
          },
        },
        () => {
          // Reinitialize calendar if profile reference is enabled
          const state = this.state as any
          if (
            state.entity.use_profile_reference &&
            this.state.selectedProfiles &&
            this.state.selectedProfiles.length > 0
          ) {
            this.initializeCalendarWithDayBased()
          }
        }
      )
    }
  }

  formatDateRange(range?: Range): string {
    if (!range || !range.startDate || !range.endDate) return ''
    return `${moment(range.startDate).format('DD MMM YYYY')} to ${moment(range.endDate).format(
      'DD MMM YYYY'
    )}`
  }

  render(): ReactNode {
    var state = this.state as any
    const isReadOnly = state.entity.status === 'success' || state.entity.status === 'processing'

    return (
      <Modal
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered'
        size='lg'
        show={this.props.show}
        backdrop={true}
        onHide={this.props.onHide}
      >
        <form
          onSubmit={(event) => {
            if (isReadOnly) {
              event.preventDefault()
              this.props.onHide()
              return
            }
            this.handleSubmit(event).then((response) => {
              if (this.props.refreshFunction) {
                const id =
                  (response &&
                    response.data &&
                    response.data.data &&
                    response.data.data.entity_id) ||
                  (response && response.data && response.data.data && response.data.data.id) ||
                  this.props.itemIdForUpdate
                this.props.refreshFunction(id)
              }
              this.props.onHide()
            })
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isReadOnly ? 'View' : this.props.itemIdForUpdate === 'add' ? 'Add' : 'Edit'}{' '}
              Simulation
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FormGroup>
              <Field
                title='Name'
                className='form-control'
                type='text'
                name='name'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.name}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Description'
                className='form-control'
                type='text'
                name='description'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.description}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup>
              <AutocompleteSelect
                key={`company-${this.props.itemIdForUpdate}-${state.entity.company}`}
                title='Company'
                name='company'
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.company || 0}
                autoCompleteTokenUrl='/company/autocomplete'
                isDisabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup>
              <Select
                title='Company Site'
                name='company_site'
                options={this.state.siteOptions || []}
                defaultValue={state.entity.company_site}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup>
              <Select
                title='Meter Type'
                name='meter_type'
                options={[
                  { value: 'Electricity', label: 'Electricity' },
                  { value: 'Gas', label: 'Gas' },
                ]}
                defaultValue={state.entity.meter_type}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup className='mb-5'>
              <label className='form-label fw-bold'>Date Range</label>
              <div className='position-relative'>
                <div
                  className='form-control'
                  style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                  onClick={(e) => {
                    if (isReadOnly) return
                    e.stopPropagation()
                    this.setState({ ...this.state, datePickerOpen: !this.state.datePickerOpen })
                  }}
                >
                  {this.formatDateRange(this.state.dateRange) || 'Select date range'}
                  {!isReadOnly && (
                    <span className='position-absolute end-0 top-50 translate-middle-y me-3'>
                      <i className='fa fa-calendar fs-4'></i>
                    </span>
                  )}
                </div>
                {this.state.datePickerOpen && !isReadOnly && (
                  <>
                    <div
                      className='position-fixed top-0 start-0 w-100 h-100'
                      style={{ zIndex: 1040 }}
                      onClick={() => this.setState({ ...this.state, datePickerOpen: false })}
                    />
                    <div
                      className='position-absolute mt-1'
                      style={{
                        zIndex: 1050,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DateRange
                        onChange={(rangesByKey) => this.handleCustomDateRangeChange(rangesByKey)}
                        moveRangeOnFirstSelection={false}
                        ranges={this.state.dateRange ? [this.state.dateRange] : []}
                        weekStartsOn={1}
                        months={1}
                        direction='horizontal'
                        preventSnapRefocus={true}
                        calendarFocus='forwards'
                        minDate={moment().startOf('day').toDate()}
                        maxDate={
                          this.state.dateRange?.startDate &&
                          this.state.dateRange?.endDate &&
                          moment(this.state.dateRange.startDate).isSame(
                            this.state.dateRange.endDate
                          )
                            ? moment(this.state.dateRange.startDate).add(30, 'days').toDate()
                            : undefined
                        }
                      />
                      <div className='d-flex gap-2 p-3 bg-white border-top'>
                        <button
                          type='button'
                          className='btn btn-sm btn-secondary'
                          onClick={() => this.setState({ ...this.state, datePickerOpen: false })}
                        >
                          Cancel
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-primary'
                          onClick={() => this.applyDateRange()}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {!isReadOnly && (
                <small className='text-muted mt-1 d-block'>
                  <i className='fa fa-info-circle me-1'></i>
                  Maximum range is 1 month.
                </small>
              )}
            </FormGroup>
            <FormGroup>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='use_profile_reference'
                  checked={state.entity.use_profile_reference || false}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    this.setState({
                      ...this.state,
                      entity: {
                        ...state.entity,
                        use_profile_reference: e.target.checked,
                      },
                    })
                  }}
                />
                <label className='form-check-label fw-bold mb-2' htmlFor='use_profile_reference'>
                  Do you want to use profile as reference?
                </label>
              </div>
            </FormGroup>
            {state.entity.use_profile_reference && (
              <>
                <FormGroup>
                  <label className='form-label fw-bold'>Profiles</label>
                  <ReactSelect
                    isMulti
                    name='profiles'
                    options={this.state.profileOptions || []}
                    value={this.state.selectedProfiles || []}
                    isDisabled={isReadOnly}
                    onChange={(selectedOptions: any) => {
                      this.setState(
                        {
                          ...this.state,
                          selectedProfiles: selectedOptions || [],
                        },
                        () => {
                          if (state.entity.start_date && state.entity.end_date) {
                            this.initializeCalendarWithDayBased()
                          }
                        }
                      )
                      const profileIds = selectedOptions
                        ? selectedOptions.map((opt: any) => opt.value)
                        : []
                      const jsonValue = JSON.stringify({ profile_ids: profileIds })
                      const syntheticEvent = {
                        target: {
                          name: 'profiles',
                          value: jsonValue,
                          type: 'text',
                        },
                      } as any
                      this.handleInputChange(syntheticEvent)
                    }}
                    className='react-select-container'
                    classNamePrefix='react-select'
                  />
                </FormGroup>
                {this.state.selectedProfiles &&
                  this.state.selectedProfiles.length > 0 &&
                  state.entity.start_date &&
                  state.entity.end_date && (
                    <div className='mt-4'>
                      <h5 className='mb-3'>Calendar Schedule</h5>
                      {/* Profiles Legend - Draggable */}
                      {!isReadOnly &&
                        this.state.selectedProfiles &&
                        this.state.selectedProfiles.length > 0 && (
                          <div className='mb-3'>
                            <strong>Profiles (Drag to Calendar):</strong>
                            <div className='d-flex flex-wrap gap-2 mt-2'>
                              {this.state.selectedProfiles.map((profile) => {
                                const isEventBased = profile.profile_type === 'event_based'
                                const badgeClass = isEventBased ? 'badge-primary' : 'badge-success'
                                const profileName = this.getProfileName(profile)
                                const detail = isEventBased ? profile.event_type_name : profile.day
                                const displayName = detail
                                  ? `${profileName} - ${detail}`
                                  : profileName
                                return (
                                  <span
                                    key={profile.value}
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('profileId', String(profile.value))
                                      e.dataTransfer.effectAllowed = 'copy'
                                    }}
                                    className={`badge ${badgeClass}`}
                                    style={{
                                      cursor: 'grab',
                                      padding: '8px 12px',
                                      fontSize: '13px',
                                    }}
                                    title={`Drag this ${
                                      isEventBased ? 'event' : 'day-based'
                                    } profile to calendar days`}
                                  >
                                    <i className='fa fa-grip-vertical me-2'></i>
                                    {displayName}
                                  </span>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      {/* Calendar Grid */}
                      <div
                        className='calendar-grid'
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(7, 1fr)',
                          gap: '8px',
                          maxHeight: '400px',
                          overflowY: 'auto',
                        }}
                      >
                        {/* Day Headers - Monday to Sunday */}
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                          <div
                            key={day}
                            className='text-center fw-bold'
                            style={{ padding: '8px', backgroundColor: '#f5f5f5' }}
                          >
                            {day}
                          </div>
                        ))}
                        {/* Calendar Dates */}
                        {(() => {
                          const dates = this.generateCalendarDates()
                          if (dates.length === 0) return null

                          const firstDate = new Date(dates[0])
                          let firstDay = firstDate.getDay()
                          // Convert Sunday (0) to 6, and shift Mon-Sat to 0-5
                          firstDay = firstDay === 0 ? 6 : firstDay - 1
                          const emptyDays = Array.from({ length: firstDay }, (_, i) => (
                            <div key={`empty-${i}`}></div>
                          ))

                          return (
                            <>
                              {emptyDays}
                              {dates.map((dateStr) => {
                                const date = new Date(dateStr)
                                const dayName = date.toLocaleDateString('en-US', {
                                  weekday: 'long',
                                })
                                const profilesOnDate =
                                  this.state.calendarSettings?.[dateStr]?.profiles || []

                                return (
                                  <div
                                    key={dateStr}
                                    className='calendar-day'
                                    onDragOver={(e) => {
                                      if (isReadOnly) return
                                      e.preventDefault()
                                      e.dataTransfer.dropEffect = 'copy'
                                    }}
                                    onDrop={(e) => {
                                      if (isReadOnly) return
                                      e.preventDefault()
                                      const profileId = Number(e.dataTransfer.getData('profileId'))
                                      if (profileId) {
                                        this.addEventToDate(dateStr, profileId)
                                      }
                                    }}
                                    style={{
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      padding: '8px',
                                      minHeight: '100px',
                                      backgroundColor: 'white',
                                    }}
                                  >
                                    <div className='text-center fw-bold mb-1'>{date.getDate()}</div>
                                    <div className='text-muted small text-center mb-2'>
                                      {dayName.substring(0, 3)}
                                    </div>
                                    <div className='d-flex flex-column gap-1'>
                                      {profilesOnDate.map((profileId: number) => {
                                        const profile = this.state.selectedProfiles?.find(
                                          (p) => p.value === profileId
                                        )
                                        if (!profile) return null
                                        const isEventBased = profile.profile_type === 'event_based'
                                        const profileName = this.getProfileName(profile)
                                        const initials = this.getProfileInitials(profileName)
                                        const detail = isEventBased
                                          ? profile.event_type_name
                                          : profile.day
                                        const displayText = detail
                                          ? `${initials} - ${detail}`
                                          : initials
                                        return (
                                          <div
                                            key={profileId}
                                            className={`badge ${
                                              isEventBased ? 'badge-primary' : 'badge-success'
                                            } d-flex justify-content-between align-items-center`}
                                            style={{
                                              fontSize: '10px',
                                              padding: '4px 6px',
                                              position: 'relative',
                                            }}
                                            title={`${profileName}${detail ? ` - ${detail}` : ''}`}
                                          >
                                            <span className='text-truncate' style={{ flex: 1 }}>
                                              {displayText.substring(0, 15)}
                                            </span>
                                            {!isReadOnly && (
                                              <button
                                                type='button'
                                                onClick={() =>
                                                  this.removeEventFromDate(dateStr, profileId)
                                                }
                                                className='btn btn-sm p-0 ms-1'
                                                style={{
                                                  border: 'none',
                                                  background: 'transparent',
                                                  color: 'white',
                                                  fontSize: '12px',
                                                  lineHeight: 1,
                                                }}
                                                title='Remove'
                                              >
                                                <i className='fa fa-times-circle'></i>
                                              </button>
                                            )}
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )
                              })}
                            </>
                          )
                        })()}
                      </div>
                    </div>
                  )}
              </>
            )}
            {/* result_data and detail are filled by backend; not editable in UI */}
            {this.props.itemIdForUpdate !== 'add' && (
              <FormGroup>
                <Field
                  title='Status'
                  className='form-control'
                  type='text'
                  name='status'
                  defaultValue={state.entity.status}
                  onInput={() => {}}
                  disabled={true}
                />
              </FormGroup>
            )}
          </Modal.Body>
          <Modal.Footer className='justify-content-end'>
            {this.props.itemIdForUpdate !== 'add' && !isReadOnly ? (
              <button
                type='button'
                onClick={(event) => {
                  this.handleDelete(event).then((response) => {
                    if (this.props.refreshFunction) {
                      this.props.refreshFunction('')
                    }
                    this.props.onHide()
                  })
                }}
                className='btn btn-danger btn-sm'
              >
                <span className='fa fa-trash'></span>
                Delete
              </button>
            ) : (
              <></>
            )}
            {!isReadOnly ? (
              <button type='submit' className='btn btn-success btn-sm'>
                <span className='fa fa-floppy-save'></span>
                Save
              </button>
            ) : (
              <button
                type='button'
                className='btn btn-secondary btn-sm'
                onClick={this.props.onHide}
              >
                Close
              </button>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    )
  }
}

export default SimulationsEditModal
